import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, CheckCircle, Loader2, RefreshCw, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function VerifyEmailPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState(null);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [manualEmail, setManualEmail] = useState('');
  
  const inputRefs = useRef([]);
  // Check multiple sources for email: state (from registration), query param, or logged-in user
  const email = location.state?.email || searchParams.get('email') || user?.email || manualEmail;

  // Cooldown timer for resend
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError(null);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace - move to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Handle paste
    if (e.key === 'v' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then(text => {
        const digits = text.replace(/\D/g, '').slice(0, 6).split('');
        const newCode = [...code];
        digits.forEach((digit, i) => {
          if (i < 6) newCode[i] = digit;
        });
        setCode(newCode);
        
        // Focus appropriate input
        const lastFilledIndex = Math.min(digits.length - 1, 5);
        inputRefs.current[lastFilledIndex]?.focus();
        
        // Auto-submit if complete
        if (digits.length === 6) {
          handleVerify(digits.join(''));
        }
      });
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text');
    const digits = text.replace(/\D/g, '').slice(0, 6).split('');
    const newCode = [...code];
    digits.forEach((digit, i) => {
      if (i < 6) newCode[i] = digit;
    });
    setCode(newCode);
    
    // Focus appropriate input
    const lastFilledIndex = Math.min(digits.length - 1, 5);
    inputRefs.current[lastFilledIndex]?.focus();
    
    // Auto-submit if complete
    if (digits.length === 6) {
      handleVerify(digits.join(''));
    }
  };

  const handleVerify = async (verificationCode) => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }
    
    setVerifying(true);
    setError(null);
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-email`, {
        email: email,
        code: verificationCode
      });
      
      setVerified(true);
      toast.success('Email verified successfully!');
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      let errorMessage = 'Verification failed. Please try again.';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      setError(errorMessage);
      toast.error(errorMessage);
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (cooldown > 0 || !email) return;
    
    setResending(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification`, {
        email: email
      });
      
      toast.success('Verification code sent to your email');
      setCooldown(60); // 60 second cooldown
      
      // If in dev mode, show the code
      if (response.data.dev_mode && response.data.verification_code) {
        toast.info(`Dev Mode - Code: ${response.data.verification_code}`, { duration: 10000 });
      }
    } catch (err) {
      let errorMessage = 'Failed to resend code';
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage);
    } finally {
      setResending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify(code.join(''));
  };

  // Show success state
  if (verified) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-full max-w-lg text-center"
        >
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1A365D]">Email Verified!</h1>
            <p className="text-slate-600 mt-2">Your email has been successfully verified.</p>
            <p className="text-slate-500 mt-4">Redirecting you to sign in...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  // Show code entry form
  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
          <div className="w-16 h-16 bg-[#00B4B4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="h-8 w-8 text-[#00B4B4]" />
          </div>
          
          <h1 className="text-2xl font-semibold text-[#1A365D] text-center">Verify Your Email</h1>
          
          {email ? (
            <p className="text-slate-600 mt-4 text-center">
              We've sent a 6-digit verification code to{' '}
              <span className="font-medium text-[#1A365D]">{email}</span>.
            </p>
          ) : (
            <div className="mt-4">
              <p className="text-slate-600 text-center mb-4">
                Enter your email address to verify your account.
              </p>
              <Input
                type="email"
                placeholder="john@example.com"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                className="mb-4"
                data-testid="verify-email-input"
              />
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="mt-8">
            <label className="block text-sm font-medium text-slate-700 text-center mb-4">
              Enter verification code
            </label>
            
            <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <Input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={`w-12 h-14 text-center text-2xl font-semibold ${
                    error ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#00B4B4]'
                  }`}
                  data-testid={`verify-code-input-${index}`}
                  disabled={verifying}
                />
              ))}
            </div>
            
            {error && (
              <div className="mt-4 flex items-center justify-center gap-2 text-red-600 text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>{error}</span>
              </div>
            )}
            
            <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }} className="mt-6">
              <Button
                type="submit"
                className="w-full bg-[linear-gradient(160deg,_#00B4B4,_#2BC3C3)] text-white py-3"
                disabled={verifying || code.some(d => d === '')}
                data-testid="verify-submit-button"
              >
                {verifying ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying...</>
                ) : (
                  'Verify Email'
                )}
              </Button>
            </motion.div>
          </form>
          
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-600 text-center mb-4">
              Didn't receive the code? Check your spam folder or click below to resend.
            </p>
            
            <Button 
              variant="outline"
              className="w-full"
              onClick={handleResendCode}
              disabled={resending || cooldown > 0}
              data-testid="verify-resend-button"
            >
              {resending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
              ) : cooldown > 0 ? (
                `Resend code in ${cooldown}s`
              ) : (
                <><RefreshCw className="h-4 w-4 mr-2" /> Resend Verification Code</>
              )}
            </Button>
          </div>
          
          <p className="text-sm text-slate-500 mt-6 text-center">
            Already verified?{' '}
            <a href="/login" className="text-[#00B4B4] hover:underline font-medium">
              Sign in
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
